import React, { FC } from 'react';
import type { ImageParams, SrcSetParam } from 'src/common/utils/imageHelpers';

import Img from '../Img';
import ErrorBoundary from '../ErrorBoundary';

type SrcSet = [minWidth: number, width: number, height: number, imgSrc?: string];

export type ImgFromSrcSetProps = {
  src: string;
  alt: string;
  loading?: 'lazy' | 'eager';
  fetchpriority?: 'high' | 'low';
  srcSet: SrcSet[];
  withRoundedCornersLg?: boolean;
  withIntrinsicSizing?: boolean;
  withPlaceholder?: boolean;
  withFullQuality?: boolean;
  withFixedLayout?: boolean;
};

const ImgFromSrcSet: FC<ImgFromSrcSetProps> = ({
  src,
  alt,
  loading,
  fetchpriority,
  srcSet,
  withRoundedCornersLg = true,
  withIntrinsicSizing = false,
  withPlaceholder = false,
  withFullQuality = false,
  withFixedLayout = false,
}) => {
  let width: number = 0;
  let height: number = 0;

  const imageParams: ImageParams = {
    q: withFullQuality ? 100 : 50,
    fit: 'crop',
  };

  const compiledSrcSet: SrcSetParam[] = [];

  srcSet.forEach(([minWidth, _width, _height, imgSrc]: SrcSet) => {
    // The srcset that defines it's minWidth as 0 is the
    // image to use in the <img/> tag
    if (minWidth === 0) {
      width = _width;
      height = _height;
      imageParams.w = _width;
      imageParams.h = _height;
    }
    compiledSrcSet.push({
      query: `only screen and (min-width: ${minWidth}px)`,
      w: _width,
      h: _height,
      imgSrc,
    });
  });

  return (
    <ErrorBoundary fallbackUI={<div>An unexpected error occurred</div>}>
      <Img
        src={src}
        alt={alt}
        loading={loading}
        width={width}
        height={height}
        srcSetParams={compiledSrcSet}
        imageParams={imageParams}
        variant={withFixedLayout ? 'fixed' : 'variable'}
        withRoundedCornersLg={withRoundedCornersLg}
        withIntrinsicSizing={withIntrinsicSizing}
        withPlaceholder={withPlaceholder}
        fetchpriority={fetchpriority}
      />
    </ErrorBoundary>
  );
};

export default ImgFromSrcSet;
